import React, { createContext, ReactNode, useContext } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IUserTypeDTO } from '@common-frontend-submodule/interfaces/user-type/iuser-type.interface';
import { IUserTypeCreationDTO } from '@common-frontend-submodule/interfaces/user-type/iuser-type-creation.interface';
import { IUserTypeUpdateDTO } from '@common-frontend-submodule/interfaces/user-type/iuser-type-update.interface';
import { IGetUserTypesRequestDTO } from '@common-frontend-submodule/interfaces/user-type/iget-user-types-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { UserTypeRoutes } from '@common-frontend-submodule/enums/routes/user-types-routes.enum';

interface UserTypeContextProps {
  getById: (userTypeId: string) => Promise<IApiResponseGenericDTO<IUserTypeDTO>>;
  filter: (model: IGetUserTypesRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IUserTypeDTO>>>;
  getAll: () => Promise<IApiResponseGenericDTO<IUserTypeDTO[]>>;
  create: (model: IUserTypeCreationDTO) => Promise<IApiResponseGenericDTO<IUserTypeDTO>>;
  update: (userTypeId: string, model: IUserTypeUpdateDTO) => Promise<IApiResponseGenericDTO<IUserTypeDTO>>;
  delete: (userTypeId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const UserTypeContext = createContext<UserTypeContextProps | undefined>(undefined);

const apiClient = ApiClient.getInstance();

export const UserTypeProvider: React.FC<{ children: ReactNode }> = ({ children }) =>  {
  const getById = async (userTypeId: string): Promise<IApiResponseGenericDTO<IUserTypeDTO>> => {
    return await apiClient.getRequest<IUserTypeDTO>(`${UserTypeRoutes.BasePrefix}/${userTypeId}`);
  };

  const filter = async (model: IGetUserTypesRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IUserTypeDTO>>> => {
    return await apiClient.postRequest<IGetUserTypesRequestDTO, IPaginatedResultDTO<IUserTypeDTO>>(
      `${UserTypeRoutes.BasePrefix}/${UserTypeRoutes.List}`,
      model
    );
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<IUserTypeDTO[]>> => {
    return await apiClient.getRequest<IUserTypeDTO[]>(`${UserTypeRoutes.BasePrefix}/${UserTypeRoutes.All}`);
  };

  const create = async (model: IUserTypeCreationDTO): Promise<IApiResponseGenericDTO<IUserTypeDTO>> => {
    return await apiClient.postRequest<IUserTypeCreationDTO, IUserTypeDTO>(UserTypeRoutes.BasePrefix, model);
  };

  const update = async (userTypeId: string, model: IUserTypeUpdateDTO): Promise<IApiResponseGenericDTO<IUserTypeDTO>> => {
    return await apiClient.putRequest<IUserTypeUpdateDTO, IUserTypeDTO>(`${UserTypeRoutes.BasePrefix}/${userTypeId}`, model);
  };

  const deleteUserType = async (userTypeId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${UserTypeRoutes.BasePrefix}/${userTypeId}`, null, { userTypeId });
  };

  return (
    <UserTypeContext.Provider value={{ getById, filter, getAll, create, update, delete: deleteUserType }}>
      {children}
    </UserTypeContext.Provider>
  );
};

export const useUserTypeStore = () => {
  const context = useContext(UserTypeContext);
  if (!context) {
    throw new Error('useUserTypeStore must be used within a UserTypeProvider');
  }
  return context;
};
